'use client'

import SignIn from '@/app/_auth/signin'
import { Navbar } from '@/components/navbar-next'
import { SuspenseFallback } from '@/components/suspense-fallback'
import { useAuthContext } from '@/hooks/use-auth-context'
import { Button } from '@mantine/core'
import { useSession } from 'next-auth/react'
import Link from 'next/link'

// Root 404, reached via the [...rest] catch-all for unknown URLs. Signed in it
// renders the full app shell (navbar) like any other page; signed out it renders
// the sign-in page with a page-not-found message. notFound() raised from within
// (main) segments (admin layout, products page) resolves to (main)/not-found.tsx
// instead, which reuses the already-mounted (main) shell.

export default function NotFound() {
  // useSession() returns undefined when this renders during SSR outside the
  // SessionProvider (e.g. a server action falling through to not-found). Guard
  // so the shell renders as unauthenticated; the client hydrates with the real
  // session.
  const { status } = useSession() ?? {}
  const { session: authSession } = useAuthContext()

  if (status === 'loading') {
    return <SuspenseFallback />
  }

  if (status !== 'authenticated') {
    return (
      <SignIn
        message="The page you are looking for does not exist. Sign in to continue."
        callbackUrl="/"
      />
    )
  }

  // Wait for the full auth context so the navbar renders with account and
  // organization data, mirroring the (main) layout.
  if (!authSession) {
    return <SuspenseFallback />
  }

  return (
    <div className="flex h-screen flex-col">
      <Navbar />
      <div className="flex min-h-0 flex-1 items-center justify-center bg-[#FAF6F2]">
        <div className="flex flex-col items-center text-center">
          <h1 className="font-heading text-6xl font-bold text-[var(--mantine-color-celerityBrand-9)]">
            404
          </h1>
          <p className="mt-3 mb-6 text-lg text-[var(--mantine-color-celerityBrand-9)]">
            The page you are looking for does not exist.
          </p>
          <Link href="/">
            <Button variant="filled">Back to Home</Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
